import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { authApi } from '../api/auth';

interface User {
  id: string;
  email: string;
  display_name: string;
  friend_code?: string;
  sharing_enabled: boolean;
  vpn_ip?: string;
  // Optional here: this User is a hand-maintained subset assembled from several
  // auth-response shapes (auth.ts) that don't carry the field. (#666)
  is_supporter?: boolean;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  oauthLogin: (provider: 'google' | 'apple' | 'github', token: string) => Promise<void>;
  handleOAuthCallback: (response: { user: User }) => Promise<void>;
  register: (email: string, password: string, display_name: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // With cookie-based auth (Issue #289), check if user is authenticated by calling /me
    // Cookies are sent automatically, no need to check localStorage
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await authApi.getCurrentUser();
      setUser(response.data);
    } catch (error) {
      // Not authenticated or session expired
      // Cookies are httpOnly, browser will handle cleanup on logout
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await authApi.login({ email, password });
      // With cookie-based auth (Issue #289), tokens are in httpOnly cookies
      // Response only contains user data
      const { user } = response.data;
      setUser(user);
    } catch (error) {
      throw error;
    }
  };

  /**
   * Complete SDK-based OAuth flow by validating token and getting user data.
   * Used for ID token flows where frontend receives token directly from OAuth SDK.
   *
   * @param provider - OAuth provider identifier
   * @param token - ID token received from OAuth SDK
   *
   * DEPRECATED: This method is no longer used as all OAuth providers now use redirect flows.
   * - GitHub OAuth: Uses redirect with code exchange (always did)
   * - Google OAuth: Switched from One Tap SDK to redirect flow (Phase 1)
   * - Apple OAuth: Switched from SDK popup to redirect flow (Phase 2)
   *
   * This method is kept for backwards compatibility but should not be used for new integrations.
   */
  const oauthLogin = async (provider: 'google' | 'apple' | 'github', token: string) => {
    try {
      const response = await authApi.oauthLogin({ provider, token });
      // With cookie-based auth (Issue #289), tokens are in httpOnly cookies
      const { user } = response.data;
      setUser(user);
    } catch (error) {
      throw error;
    }
  };

  /**
   * Complete redirect-based OAuth flow by updating user state.
   * Used for authorization code flows where backend returns AuthResponse.
   *
   * @param response - Authentication response from OAuth callback endpoint
   *
   * With cookie-based auth (Issue #289), tokens are set as httpOnly cookies by backend.
   * Frontend only needs to update user state.
   *
   * Current usage: All OAuth providers (GitHub, Google, Apple)
   */
  const handleOAuthCallback = useCallback(async (response: { user: User }) => {
    // Tokens are already in httpOnly cookies set by backend
    setUser(response.user);
  }, []);

  const register = async (email: string, password: string, display_name: string) => {
    try {
      await authApi.register({ email, password, display_name });
      // Don't auto-login - user must verify email first
      // The Register component will navigate to email verification pending screen
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      // Call backend logout endpoint to clear httpOnly cookies (Issue #289)
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
      // Continue with local logout even if backend call fails
    } finally {
      setUser(null);
    }
  };

  const refreshUser = async () => {
    try {
      const response = await authApi.getCurrentUser();
      setUser(response.data);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  const value = {
    user,
    isLoading,
    login,
    oauthLogin,
    handleOAuthCallback,
    register,
    logout,
    refreshUser,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};